import { useState } from 'react'
import { clsx } from 'clsx'
import { Check, X, Minus } from 'lucide-react'

interface ChecklistItem {
  id: string
  label: string
  category: string
  required: boolean
}

interface InspectionChecklistProps {
  items: ChecklistItem[]
  onComplete: (results: Record<string, 'pass' | 'fail' | 'na'>) => void
}

const STATUSES = [
  {
    key: 'pass' as const,
    label: 'OK',
    icon: Check,
    active: 'bg-emerald-600 text-white border-emerald-600 shadow-sm',
    idle: 'bg-white text-slate-500 border-slate-200 hover:border-emerald-400 hover:text-emerald-600',
  },
  {
    key: 'fail' as const,
    label: 'Avvik',
    icon: X,
    active: 'bg-red-600 text-white border-red-600 shadow-sm',
    idle: 'bg-white text-slate-500 border-slate-200 hover:border-red-400 hover:text-red-600',
  },
  {
    key: 'na' as const,
    label: 'N/A',
    icon: Minus,
    active: 'bg-slate-500 text-white border-slate-500 shadow-sm',
    idle: 'bg-white text-slate-400 border-slate-200 hover:border-slate-400',
  },
]

const InspectionChecklist = ({ items, onComplete }: InspectionChecklistProps) => {
  const [results, setResults] = useState<Record<string, 'pass' | 'fail' | 'na'>>({})

  const handleItemClick = (itemId: string, status: 'pass' | 'fail' | 'na') => {
    setResults(prev => {
      if (prev[itemId] === status) {
        const next = { ...prev }
        delete next[itemId]
        return next
      }
      return { ...prev, [itemId]: status }
    })
  }

  const allRequiredCompleted = items
    .filter(item => item.required)
    .every(item => results[item.id])

  const answeredCount = items.filter(item => results[item.id]).length
  const totalCount = items.length

  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = []
    acc[item.category].push(item)
    return acc
  }, {} as Record<string, ChecklistItem[]>)

  return (
    <div className="flex flex-col gap-5">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#1E3A5F] rounded-full transition-all duration-300"
            style={{ width: `${totalCount > 0 ? (answeredCount / totalCount) * 100 : 0}%` }}
          />
        </div>
        <span className="text-sm font-medium text-slate-500 shrink-0">
          {answeredCount}/{totalCount}
        </span>
      </div>

      {/* Checklist groups */}
      {Object.entries(groupedItems).map(([category, categoryItems]) => (
        <div key={category}>
          {/* Category header */}
          <div className="flex items-center gap-3 mb-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
              {category}
            </p>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div className="flex flex-col gap-2">
            {categoryItems.map(item => {
              const current = results[item.id]
              return (
                <div
                  key={item.id}
                  className={clsx(
                    'bg-white border rounded-lg px-4 py-3 flex items-center gap-4 transition-colors',
                    current === 'fail'
                      ? 'border-red-200 bg-red-50/40'
                      : current === 'pass'
                      ? 'border-emerald-200 bg-emerald-50/30'
                      : 'border-slate-200'
                  )}
                >
                  {/* Label */}
                  <div className="flex-1 min-w-0">
                    <span className="text-base font-medium text-slate-800">
                      {item.label}
                    </span>
                    {item.required && (
                      <span className="text-red-500 ml-1 text-sm">*</span>
                    )}
                  </div>

                  {/* Pass / Avvik / N/A buttons */}
                  <div className="flex items-center gap-2 shrink-0">
                    {STATUSES.map(({ key, label, icon: Icon, active, idle }) => (
                      <button
                        key={key}
                        onClick={() => handleItemClick(item.id, key)}
                        className={clsx(
                          'flex items-center gap-1.5 px-4 rounded-md border font-semibold text-sm transition-colors',
                          current === key ? active : idle
                        )}
                        style={{ minHeight: 44, minWidth: 72 }}
                      >
                        <Icon size={16} />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Sticky footer */}
      <div
        className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 px-4 py-3"
        style={{ marginLeft: '-1.5rem', marginRight: '-1.5rem', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}
      >
        <button
          disabled={!allRequiredCompleted}
          onClick={() => onComplete(results)}
          className="w-full flex items-center justify-center gap-2 bg-[#1E3A5F] text-white text-base font-semibold rounded-md hover:bg-[#152C4A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
          style={{ minHeight: 52 }}
        >
          <Check size={18} />
          Fullfør inspeksjon
        </button>
      </div>
    </div>
  )
}

export default InspectionChecklist
